import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, FolderOpen, Check, Plus } from 'lucide-react'
import { useProject } from '../../contexts/ProjectContext'

export default function ProjectSelector() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { currentProject, setCurrentProject, projects, isLoading } = useProject()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectProject = (project: typeof currentProject) => {
    setCurrentProject(project)
    setIsOpen(false)
  }

  const handleManageProjects = () => {
    setIsOpen(false)
    navigate('/projects')
  }

  if (isLoading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse bg-gray-200 h-8 rounded" />
      </div>
    )
  }

  return (
    <div ref={dropdownRef} className="relative px-3 py-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-2 text-left text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate text-gray-200">
            {currentProject?.name || 'Wybierz projekt'}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-3 right-3 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-auto">
          {projects.length > 0 ? (
            <>
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleSelectProject(project)}
                  className="w-full flex items-center justify-between p-3 text-left text-sm hover:bg-gray-50 first:rounded-t-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {project.name}
                        {project.isShared && (
                          <span className="ml-1 text-xs font-normal text-purple-500">(udostepniony)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {project.documentsCount} dokumentow
                      </div>
                    </div>
                  </div>
                  {currentProject?.id === project.id && (
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  )}
                </button>
              ))}
              <div className="border-t">
                <button
                  onClick={handleManageProjects}
                  className="w-full flex items-center gap-2 p-3 text-left text-sm text-gray-600 hover:bg-gray-50 rounded-b-lg"
                >
                  <Plus className="w-4 h-4" />
                  <span>Zarządzaj projektami</span>
                </button>
              </div>
            </>
          ) : (
            <div className="p-4 text-center">
              <p className="text-sm text-gray-500 mb-2">Brak projektów</p>
              <button
                onClick={handleManageProjects}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Utwórz projekt
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
